import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { Purchase } from "../services/procurementService";
import { STOREFRONT_HOST } from "../config/storefront";
import { ensureFonts, PO_THEME } from "./purchaseOrderPdf/theme";
import { buildPurchaseOrderPages, imagesReady, PAGE_H, PAGE_W } from "./purchaseOrderPdf/pages";
import { collectTextRuns } from "./purchaseOrderPdf/textLayer";
import { toCopyableText } from "./purchaseOrderPdf/layout";
import { loadThumbnails } from "./purchaseOrderPdf/thumbnails";

export interface ColumnConfig {
    key: string;
    label: string;
    enabled: boolean;
    align?: "left" | "center" | "right";
}

export interface HeaderFieldConfig {
    key: string;
    label: string;
    enabled: boolean;
}

export const DEFAULT_HEADER_FIELDS: HeaderFieldConfig[] = [
    { key: "po_number", label: "PO Number", enabled: false },
    { key: "date", label: "Date", enabled: true },
    { key: "supplier", label: "Supplier", enabled: true },
    { key: "status", label: "Status", enabled: false },
    { key: "expected_delivery", label: "Expected Delivery", enabled: false },
];

export const DEFAULT_PURCHASE_ORDER_COLUMNS: ColumnConfig[] = [
    { key: "sl", label: "SL", enabled: true, align: "center" },
    { key: "image", label: "Photo", enabled: true, align: "center" },
    { key: "product", label: "Product Name", enabled: true, align: "left" },
    { key: "sku", label: "SKU", enabled: false, align: "center" },
    {
        key: "qty_ordered",
        label: "Qty Ordered",
        enabled: true,
        align: "center",
    },
    {
        key: "qty_received",
        label: "Qty Received",
        enabled: false,
        align: "center",
    },
    { key: "unit_cost", label: "Unit Cost", enabled: true, align: "right" },
    { key: "total_cost", label: "Total Cost", enabled: true, align: "right" },
];

export interface CompanyInfo {
    name: string;
    phone?: string;
    email?: string;
    whatsapp?: string;
    /** The address on one line (older callers and other reports). */
    address?: string;
    /** The address as its separate lines — English and Bangla lines are
     * drawn in their own fonts on the purchase-order PDF. */
    addressLines?: string[];
    logoUrl?: string;
    /** "catelynpetshop.com", shown in the header and footer. */
    website?: string;
}

export const DEFAULT_COMPANY_INFO: CompanyInfo = {
    name: "Catelyn Pet Shop",
    phone: "01800 072 072",
    address: "Uttara, Dhaka",
    logoUrl: "/images/logo192.png",
    website: STOREFRONT_HOST,
};

const A4_MM = { width: 210, height: 297 };
const MM_PER_PX = A4_MM.width / PAGE_W;
const PT_PER_PX = 0.75;

/**
 * The purchase order as a PDF, in the "Order Request" design (see
 * ./purchaseOrderPdf). Each page is drawn from HTML into an image, so it
 * looks exactly like the design — the shop's fonts and the Bangla address
 * included — and an invisible copy of its text is laid on top, so the
 * PDF's text can still be selected, copied and searched.
 */
export class PurchaseOrderPDFGenerator {
    private doc: jsPDF;
    private companyInfo: CompanyInfo;

    constructor(companyInfo?: CompanyInfo) {
        this.doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
        this.companyInfo = { ...DEFAULT_COMPANY_INFO, ...(companyInfo ?? {}) };
        this.companyInfo.website ??= STOREFRONT_HOST;
    }

    private async loadLogo(): Promise<string | null> {
        try {
            const logoUrl = this.companyInfo.logoUrl || "/images/logo192.png";
            const response = await fetch(logoUrl);
            if (!response.ok) return null;
            const blob = await response.blob();
            return new Promise((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result as string);
                reader.onerror = () => resolve(null);
                reader.readAsDataURL(blob);
            });
        } catch (error) {
            console.error("Failed to load logo:", error);
            return null;
        }
    }

    /** Lays each text run invisibly over the page image, stretched to the
     * same width as the visible text so a selection lines up with it. */
    private addTextLayer(page: HTMLElement) {
        this.doc.setFont("helvetica", "normal");
        collectTextRuns(page).forEach((run) => {
            const core = toCopyableText(run.text);
            if (!core) return;
            // Keep a drawn space at either end, so neighbouring runs copy apart.
            const text = `${/^\s/.test(run.text) ? " " : ""}${core}${/\s$/.test(run.text) ? " " : ""}`;
            this.doc.setFontSize(run.fontPx * PT_PER_PX);
            // Stretch the glyphs (not the gaps between them) so the hidden
            // text covers the same width as the visible text; gaps would read
            // as spaces when copied ("P U R C H A S E").
            const natural = this.doc.getTextWidth(text);
            const scale = natural > 0 ? (run.width * MM_PER_PX) / natural : 1;
            this.doc.text(text, run.x * MM_PER_PX, run.y * MM_PER_PX, {
                baseline: "top",
                renderingMode: "invisible",
                horizontalScale: Math.min(Math.max(scale, 0.3), 3),
            });
        });
    }

    async generate(
        purchase: Purchase,
        columns: ColumnConfig[] = DEFAULT_PURCHASE_ORDER_COLUMNS,
        headerFields: HeaderFieldConfig[] = DEFAULT_HEADER_FIELDS,
        includeShipping: boolean = false,
        showSummary: boolean = true,
    ): Promise<jsPDF> {
        const showPhotos = columns.some((c) => c.key === "image" && c.enabled);
        const [logoDataUrl, thumbnails] = await Promise.all([
            this.loadLogo(),
            showPhotos ? loadThumbnails(purchase.items) : Promise.resolve(undefined),
            ensureFonts(),
        ]);
        const { container, pages } = buildPurchaseOrderPages(purchase, {
            columns,
            headerFields,
            includeShipping,
            showSummary,
            companyInfo: this.companyInfo,
            logoDataUrl,
            generatedAt: new Date(),
            thumbnails,
        });
        try {
            await imagesReady(container);
            for (let i = 0; i < pages.length; i++) {
                const canvas = await html2canvas(pages[i], {
                    scale: 2,
                    useCORS: true,
                    backgroundColor: PO_THEME.page,
                    logging: false,
                    width: PAGE_W,
                    height: PAGE_H,
                });
                if (i > 0) this.doc.addPage();
                this.doc.addImage(canvas.toDataURL("image/jpeg", 0.92), "JPEG", 0, 0, A4_MM.width, A4_MM.height);
                this.addTextLayer(pages[i]);
            }
        } finally {
            container.remove();
        }
        return this.doc;
    }

    async download(
        purchase: Purchase,
        columns: ColumnConfig[] = DEFAULT_PURCHASE_ORDER_COLUMNS,
        headerFields: HeaderFieldConfig[] = DEFAULT_HEADER_FIELDS,
        includeShipping: boolean = false,
        showSummary: boolean = true,
        filename?: string,
    ): Promise<void> {
        await this.generate(purchase, columns, headerFields, includeShipping, showSummary);
        this.doc.save(filename || `PO_${purchase.purchase_number}.pdf`);
    }

    /** A link to the PDF for opening in a new tab. (A blob link — browsers
     * refuse to open "data:" PDFs in a new tab.) */
    async preview(
        purchase: Purchase,
        columns: ColumnConfig[] = DEFAULT_PURCHASE_ORDER_COLUMNS,
        headerFields: HeaderFieldConfig[] = DEFAULT_HEADER_FIELDS,
        includeShipping: boolean = false,
        showSummary: boolean = true,
    ): Promise<string> {
        await this.generate(purchase, columns, headerFields, includeShipping, showSummary);
        return String(this.doc.output("bloburl"));
    }
}
